import { stripHtml } from "utils/strings/shortenLongDescription";

export type BillingMode = "full" | "percentage" | "fixed_amount";
export type JobBillingStatusValue =
  | "ready_to_bill"
  | "partial_billed"
  | "deposit_billed"
  | "pending_billing"
  | "billed"
  | "partial_paid"
  | "paid";
export type LineSourceType = "job_phase" | "manual" | "seed_fallback";

export type NormalizableJob = {
  id: string | number;
  externalId: string;
  proposedOverallTotal?: unknown;
  proposalStage?: {
    name?: string | null;
  } | null;
  jobPhases?: Array<any> | null;
};

export type SyncablePhase = {
  sourceKey: string;
  jobId: string;
  jobExternalId: string;
  jobName: string;
  phaseId: string;
  phaseName: string;
  groupId?: string | null;
  groupName?: string | null;
  amountCents: number;
  description: string;
};

export type SyncableJob = {
  id: string;
  externalId: string;
  name: string;
  totalCents: number;
  phases: SyncablePhase[];
};

export type EditableLine = {
  id: string;
  sourceType: LineSourceType;
  sourceKey: string;
  jobId?: string;
  jobPhaseId?: string;
  jobExternalId?: string;
  jobName?: string;
  proposalProductGroupId?: string | null;
  groupName?: string | null;
  itemName: string;
  itemDescription: string;
  amountInput: string;
  syncAmountInput?: string;
  hasSyncAmountOverride?: boolean;
  removed: boolean;
};

export type QuickbooksFormItem = {
  itemsName?: string | null;
  itemsDescription?: string | null;
  itemAmount?: string | number | null;
};

export type AllocationPreview = {
  selectedTotal: number;
  billedTotal: number;
  byLine: Map<string, number>;
};

export const DEFAULT_BILLING_STATUSES: JobBillingStatusValue[] = [
  "ready_to_bill",
  "partial_billed",
  "deposit_billed",
  "pending_billing",
  "billed",
  "partial_paid",
  "paid",
];

export function normalizeBillingStatusValue(value: string): JobBillingStatusValue {
  const normalized = String(value || "")
    .trim()
    .toLowerCase() as JobBillingStatusValue;

  if (DEFAULT_BILLING_STATUSES.includes(normalized)) {
    return normalized;
  }

  return "billed";
}

export function toQuickbooksBillingModeEnum(
  mode: BillingMode
): "FULL" | "PERCENTAGE" | "FIXED_AMOUNT" {
  switch (mode) {
    case "full":
      return "FULL";
    case "percentage":
      return "PERCENTAGE";
    case "fixed_amount":
      return "FIXED_AMOUNT";
  }
}

export function toJobBillingStatusEnum(status: JobBillingStatusValue) {
  return status.toUpperCase() as
    | "READY_TO_BILL"
    | "PARTIAL_BILLED"
    | "DEPOSIT_BILLED"
    | "PENDING_BILLING"
    | "BILLED"
    | "PARTIAL_PAID"
    | "PAID";
}

export function asNumber(value: unknown): number {
  if (typeof value === "number") {
    return value;
  }

  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function formatAmountInputFromCents(value: number) {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

export function parseAmountInputToCents(value: string) {
  const normalized = value.replace(/[^0-9.-]/g, "");
  const parsed = Number(normalized || 0);
  if (!Number.isFinite(parsed)) {
    return 0;
  }

  return Math.round(parsed * 100);
}

export function resolveProposalProductDescription(phase: any, fallback: string) {
  const html =
    phase?.proposalProduct?.statementOfWork ||
    phase?.proposalProduct?.material?.statementOfWork ||
    "";

  const strippedHtmlValue = typeof html === "string" ? stripHtml(html) : "";
  const stripped = typeof strippedHtmlValue === "string" ? strippedHtmlValue.trim() : "";
  return stripped || fallback;
}

export function normalizeSyncableJob(job: NormalizableJob): SyncableJob {
  const jobId = String(job.id);
  const jobName = job.proposalStage?.name || `Job ${job.externalId}`;
  const phases = (job.jobPhases ?? []).map((phase) => {
    const itemName =
      phase?.proposalProduct?.material?.externalProductName ||
      phase?.proposalProduct?.material?.name ||
      phase?.proposalProduct?.productName ||
      phase.productName ||
      "Line Item";
    const description = resolveProposalProductDescription(
      phase,
      `Job ID: ${job.id}, Job Phase ID: ${phase.id}, Job Name: ${jobName}`
    );

    return {
      sourceKey: `phase:${jobId}:${phase.id}`,
      jobId,
      jobExternalId: job.externalId,
      jobName,
      phaseId: String(phase.id),
      phaseName: itemName,
      groupId: phase.proposalProductGroup?.id ? String(phase.proposalProductGroup.id) : null,
      groupName: phase.proposalProductGroup?.name || null,
      amountCents: asNumber(phase.proposedOverallTotal),
      description,
    };
  });

  return {
    id: jobId,
    externalId: job.externalId,
    name: jobName,
    totalCents: asNumber(job.proposedOverallTotal),
    phases,
  };
}

export function createGeneratedLine(phase: SyncablePhase): EditableLine {
  return {
    id: phase.sourceKey,
    sourceType: "job_phase",
    sourceKey: phase.sourceKey,
    jobId: phase.jobId,
    jobPhaseId: phase.phaseId,
    jobExternalId: phase.jobExternalId,
    jobName: phase.jobName,
    proposalProductGroupId: phase.groupId ?? undefined,
    groupName: phase.groupName ?? undefined,
    itemName: phase.phaseName,
    itemDescription: phase.description,
    amountInput: formatAmountInputFromCents(phase.amountCents),
    removed: false,
  };
}

export function shouldKeepExistingDescription(line: EditableLine) {
  const description = line.itemDescription?.trim() || "";

  if (!description) {
    return false;
  }

  if (description.startsWith("Job ID:")) {
    return false;
  }

  return true;
}

export function createFallbackLinesFromFormData({
  items,
  seedJobId,
  seedJobExternalId,
  seedJobName,
}: {
  items: QuickbooksFormItem[];
  seedJobId: string;
  seedJobExternalId: string;
  seedJobName: string;
}) {
  return items.map((item, index) => ({
    id: `fallback:${seedJobId}:${index}`,
    sourceType: "seed_fallback" as const,
    sourceKey: `fallback:${seedJobId}:${index}`,
    jobId: seedJobId,
    jobExternalId: seedJobExternalId,
    jobName: seedJobName,
    itemName: item.itemsName || `Line ${index + 1}`,
    itemDescription: item.itemsDescription || "",
    amountInput: formatAmountInputFromCents(asNumber(item.itemAmount)),
    removed: false,
  }));
}

export function createAggregateFallbackLine({
  seedJobId,
  seedJobExternalId,
  seedJobName,
  amountCents,
}: {
  seedJobId: string;
  seedJobExternalId: string;
  seedJobName: string;
  amountCents: number;
}): EditableLine[] {
  if (amountCents <= 0) {
    return [];
  }

  return [
    {
      id: `fallback:${seedJobId}:aggregate`,
      sourceType: "seed_fallback",
      sourceKey: `fallback:${seedJobId}:aggregate`,
      jobId: seedJobId,
      jobExternalId: seedJobExternalId,
      jobName: seedJobName,
      itemName: seedJobName,
      itemDescription: "",
      amountInput: formatAmountInputFromCents(amountCents),
      removed: false,
    },
  ];
}

export function mergeEditableLines(currentLines: EditableLine[], selectedJobs: SyncableJob[]) {
  const currentBySourceKey = new Map(currentLines.map((line) => [line.sourceKey, line] as const));
  const generatedLines = selectedJobs.flatMap((selectedJob) =>
    selectedJob.phases.map((phase) => {
      const existingLine = currentBySourceKey.get(phase.sourceKey);
      const nextBase = createGeneratedLine(phase);

      if (!existingLine) {
        return nextBase;
      }

      return {
        ...nextBase,
        itemName: existingLine.itemName,
        itemDescription: shouldKeepExistingDescription(existingLine)
          ? existingLine.itemDescription
          : nextBase.itemDescription,
        amountInput: existingLine.amountInput,
        syncAmountInput: existingLine.syncAmountInput,
        hasSyncAmountOverride: existingLine.hasSyncAmountOverride,
        removed: existingLine.removed,
      };
    })
  );

  const manualLines = currentLines.filter((line) => line.sourceType === "manual");
  const fallbackLines = currentLines.filter((line) => line.sourceType === "seed_fallback");
  const generatedJobIds = new Set(generatedLines.map((line) => line.jobId).filter(Boolean));
  const preservedFallbackLines = fallbackLines.filter((line) => {
    if (!line.jobId) {
      return true;
    }

    return !generatedJobIds.has(line.jobId);
  });

  return [...generatedLines, ...preservedFallbackLines, ...manualLines];
}

export function allocateAmounts(
  lines: Array<{ key: string; amount: number; overrideAmount?: number }>,
  mode: BillingMode,
  percentage: string,
  fixedAmount: string
): AllocationPreview {
  const selectedTotal = lines.reduce((sum, line) => sum + line.amount, 0);
  const cleanPercentage = Number(percentage || 0);
  const fixedAmountCents = parseAmountInputToCents(fixedAmount);

  let billedTotal = selectedTotal;
  if (mode === "percentage") {
    billedTotal = Math.round(selectedTotal * (cleanPercentage / 100));
  } else if (mode === "fixed_amount") {
    billedTotal = fixedAmountCents;
  }

  if (selectedTotal <= 0) {
    return {
      selectedTotal: 0,
      billedTotal: 0,
      byLine: new Map<string, number>(),
    };
  }

  if (billedTotal >= selectedTotal) {
    const byLine = new Map(lines.map((line) => [line.key, line.amount]));
    return {
      selectedTotal,
      billedTotal: applySyncAmountOverrides(lines, byLine),
      byLine,
    };
  }

  const allocations = lines.map((line) => {
    const exact = (billedTotal * line.amount) / selectedTotal;
    const floorValue = Math.floor(exact);

    return {
      key: line.key,
      floorValue,
      remainder: exact - floorValue,
    };
  });

  allocations.sort((a, b) => b.remainder - a.remainder);
  let remaining = billedTotal - allocations.reduce((sum, line) => sum + line.floorValue, 0);
  const byLine = new Map<string, number>();

  for (const line of allocations) {
    const nextValue = line.floorValue + (remaining > 0 ? 1 : 0);
    if (remaining > 0) {
      remaining -= 1;
    }
    byLine.set(line.key, nextValue);
  }

  return {
    selectedTotal,
    billedTotal: applySyncAmountOverrides(lines, byLine),
    byLine,
  };
}

function applySyncAmountOverrides(
  lines: Array<{ key: string; amount: number; overrideAmount?: number }>,
  byLine: Map<string, number>
) {
  let billedTotal = 0;

  for (const line of lines) {
    const amount = line.overrideAmount ?? byLine.get(line.key) ?? 0;
    byLine.set(line.key, amount);
    billedTotal += amount;
  }

  return billedTotal;
}
